import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Loader2,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { INGEST_WS_URL } from "@/recorder/context";
import { ingestHttpBase } from "@/lib/ingest";
import { formatClock } from "@/lib/export";
import { errorMessage } from "@/lib/ui";

/**
 * Lecture playback, PLAN.md §12 (Phase 8).
 *
 * The audio never entered Convex - ingest wrote it to disk and serves it back
 * over `/audio/<recordingId>` with a short-lived HMAC. So the element's `src`
 * is minted on mount and the token, not a cookie, is what authorizes it.
 *
 * Implements a custom, jitter-free audio player with stable duration display,
 * custom scrubber, and fluid framer-motion micro-interactions.
 */

export interface AudioPlayerHandle {
  /** Seeks and starts playing. Used by the transcript's timestamp buttons. */
  seekTo: (ms: number) => void;
}

interface AudioPlayerProps {
  recordingId: Id<"recordings">;
  /** Known duration in milliseconds from the recording document. Prevents end-time jitter on load. */
  durationMs?: number;
  /** Set once the retention sweep removed the file (Phase 9). */
  audioDeletedAt?: number;
  hasAudio: boolean;
  onTimeChange?: (ms: number) => void;
}

const SPEEDS = [1, 1.25, 1.5, 1.75, 2];

export default forwardRef<AudioPlayerHandle, AudioPlayerProps>(function AudioPlayer(
  { recordingId, durationMs, audioDeletedAt, hasAudio, onTimeChange },
  ref,
) {
  const issueToken = useAction(api.recordings.issuePlaybackToken);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);

  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);
  const [hasChangedSpeed, setHasChangedSpeed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [metaDurationMs, setMetaDurationMs] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekTimeMs, setSeekTimeMs] = useState(0);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (!hasAudio || audioDeletedAt !== undefined) return;
    let cancelled = false;
    const base = ingestHttpBase(INGEST_WS_URL);
    if (base === "") {
      setError("VITE_INGEST_WS_URL is not configured.");
      return;
    }
    void issueToken({ id: recordingId })
      .then(({ token }) => {
        if (cancelled) return;
        setSrc(`${base}/audio/${recordingId}?token=${encodeURIComponent(token)}`);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(errorMessage(caught, "Could not open the audio."));
      });
    return () => {
      cancelled = true;
    };
  }, [recordingId, hasAudio, audioDeletedAt, issueToken]);

  const totalDurationMs = durationMs ?? (metaDurationMs > 0 ? metaDurationMs : 0);
  const displayedTimeMs = isSeeking ? seekTimeMs : currentTimeMs;
  const progressPercent =
    totalDurationMs > 0
      ? Math.min(100, Math.max(0, (displayedTimeMs / totalDurationMs) * 100))
      : 0;

  useImperativeHandle(
    ref,
    () => ({
      seekTo: (ms: number) => {
        const element = audioRef.current;
        if (element === null) return;
        element.currentTime = Math.max(0, ms / 1_000);
        setCurrentTimeMs(ms);
        void element.play().catch(() => undefined);
      },
    }),
    [],
  );

  const handleTimeUpdate = useCallback(() => {
    const element = audioRef.current;
    if (element === null || isSeeking) return;
    const ms = element.currentTime * 1_000;
    setCurrentTimeMs(ms);
    onTimeChange?.(ms);
  }, [onTimeChange, isSeeking]);

  const updateBuffered = useCallback(() => {
    const element = audioRef.current;
    if (!element || totalDurationMs <= 0) return;
    if (element.buffered.length > 0) {
      const endSec = element.buffered.end(element.buffered.length - 1);
      setBufferedPercent(
        Math.min(100, Math.max(0, (endSec / (totalDurationMs / 1_000)) * 100)),
      );
    }
  }, [totalDurationMs]);

  const togglePlay = () => {
    const element = audioRef.current;
    if (element === null || src === null) return;
    if (element.paused) {
      void element.play().catch(() => undefined);
    } else {
      element.pause();
    }
  };

  const getProgressFromPointer = (clientX: number) => {
    const bar = progressBarRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (src === null || totalDurationMs <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsSeeking(true);
    const progress = getProgressFromPointer(e.clientX);
    setSeekTimeMs(progress * totalDurationMs);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isSeeking || totalDurationMs <= 0) return;
    const progress = getProgressFromPointer(e.clientX);
    setSeekTimeMs(progress * totalDurationMs);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isSeeking) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    const progress = getProgressFromPointer(e.clientX);
    const targetMs = progress * totalDurationMs;
    setIsSeeking(false);
    if (audioRef.current !== null) {
      audioRef.current.currentTime = targetMs / 1_000;
      setCurrentTimeMs(targetMs);
      onTimeChange?.(targetMs);
    }
  };

  const handleSpeedChange = (val: number) => {
    setSpeed(val);
    if (audioRef.current !== null) {
      audioRef.current.playbackRate = val;
    }
  };

  const toggleMute = () => {
    const element = audioRef.current;
    if (!element) return;
    const next = !isMuted;
    element.muted = next;
    setIsMuted(next);
  };

  if (audioDeletedAt !== undefined) {
    return (
      <p className="font-mono text-[11px] text-neutral-500">
        Audio removed on{" "}
        {new Date(audioDeletedAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })}{" "}
        by the retention policy. The transcript and summary are kept.
      </p>
    );
  }

  if (!hasAudio) {
    return (
      <p className="font-mono text-[11px] text-neutral-500">
        No stored audio for this recording yet.
      </p>
    );
  }

  if (error !== null) {
    return <p className="font-mono text-[11px] text-red-600 dark:text-red-400">{error}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-xl border border-[#d1d1cf] bg-[#fafaf8] p-3 dark:border-[#2a2a28] dark:bg-[#111110] transition-colors">
        {/* Top row: controls, timestamps, speed, volume */}
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            {/* Round Play/Pause button */}
            <motion.button
              type="button"
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              disabled={src === null}
              onClick={togglePlay}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[#222] text-white shadow-xs transition-opacity disabled:cursor-not-allowed disabled:opacity-40 dark:bg-[#ededeb] dark:text-[#141413]"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              <AnimatePresence mode="wait" initial={false}>
                {src === null ? (
                  <motion.span
                    key="loading"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.12 }}
                  >
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />
                  </motion.span>
                ) : isPlaying ? (
                  <motion.span
                    key="pause"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.12 }}
                  >
                    <Pause className="h-3.5 w-3.5 fill-current" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="play"
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.12 }}
                  >
                    <Play className="h-3.5 w-3.5 fill-current ml-0.5" />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Timestamps: completely stable, duration known upfront without jitter */}
            <div className="ml-1 flex items-baseline gap-1 font-mono text-xs tabular-nums select-none">
              <span className="font-medium text-[#222] dark:text-[#ededeb]">
                {formatClock(displayedTimeMs)}
              </span>
              <span className="text-neutral-400 dark:text-neutral-600">/</span>
              <span className="text-neutral-500 dark:text-neutral-400">
                {totalDurationMs > 0 ? formatClock(totalDurationMs) : "--:--"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            {/* Speed selector with fluid spring pill that doesn't pop in out of nowhere */}
            <div className="flex items-center gap-0.5 rounded-lg bg-neutral-200/50 p-0.5 dark:bg-neutral-800/60">
              {SPEEDS.map((option) => (
                <motion.button
                  key={option}
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setHasChangedSpeed(true);
                    handleSpeedChange(option);
                  }}
                  className={[
                    "relative cursor-pointer rounded px-1.5 py-0.5 font-mono text-[10px] transition-colors",
                    speed === option
                      ? "font-semibold text-neutral-900 dark:text-neutral-100"
                      : "text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100",
                  ].join(" ")}
                >
                  {speed === option && (
                    hasChangedSpeed ? (
                      <motion.span
                        layoutId="audio-speed-pill"
                        className="absolute inset-0 rounded bg-white shadow-xs dark:bg-neutral-700"
                        transition={{ type: "spring", stiffness: 450, damping: 35 }}
                      />
                    ) : (
                      <span className="absolute inset-0 rounded bg-white shadow-xs dark:bg-neutral-700" />
                    )
                  )}
                  <span className="relative z-10">{option}×</span>
                </motion.button>
              ))}
            </div>

            {/* Volume mute toggle */}
            <motion.button
              type="button"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              onClick={toggleMute}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-200/60 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100 transition-colors"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeX className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
              ) : (
                <Volume2 className="h-3.5 w-3.5" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Custom Scrubber progress bar */}
        <div
          ref={progressBarRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          className="group relative mt-1 flex h-7 cursor-pointer touch-none select-none items-center py-1.5"
        >
          {/* Base track */}
          <div className="relative h-1.5 w-full rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden transition-all group-hover:h-2">
            {/* Buffered bar */}
            <div
              className="absolute top-0 bottom-0 left-0 bg-neutral-300/80 dark:bg-neutral-700/80 transition-all rounded-full"
              style={{ width: `${bufferedPercent}%` }}
            />
            {/* Played progress */}
            <div
              className="absolute top-0 bottom-0 left-0 bg-[#222] dark:bg-[#ededeb] rounded-full transition-none"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {/* Thumb */}
          <div
            className="pointer-events-none absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3.5 w-3.5 rounded-full bg-[#222] dark:bg-[#ededeb] shadow-sm transition-transform group-hover:scale-125"
            style={{ left: `${progressPercent}%` }}
          />
        </div>

        {/* Audio element without native controls to prevent browser-level duration jitter */}
        <audio
          ref={audioRef}
          src={src ?? undefined}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={() => {
            if (audioRef.current && Number.isFinite(audioRef.current.duration)) {
              setMetaDurationMs(audioRef.current.duration * 1_000);
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onProgress={updateBuffered}
          onError={() =>
            setError("The audio could not be loaded - the link may have expired.")
          }
        />
      </div>
    </div>
  );
});
